/**
 * Signup page component with new design system.
 */

import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { Logo } from "../components/brand/Logo";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { Checkbox } from "../components/ui/Checkbox";
import { PasswordStrengthIndicator } from "../components/auth/PasswordStrengthIndicator";
import { Cpu, Brain, Sparkles } from "lucide-react";
import DotField from "../components/DotField";

export const Signup: React.FC = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [firstNameError, setFirstNameError] = useState<string | null>(null);
  const [lastNameError, setLastNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [showAgreements, setShowAgreements] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [agreedEula, setAgreedEula] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const accessCode = sessionStorage.getItem("accessCode");

  const { signup, isAuthenticated, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
    } else if (!accessCode) {
      navigate("/invite", { replace: true });
    }
  }, [isAuthenticated, accessCode, navigate]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const validateEmail = (email: string): boolean => {
    // Loose email validation - just check for @ and basic format
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);
    setFormError(null);

    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedEmail = email.trim();
    const trimmedCompany = company.trim();

    setFirstNameError(trimmedFirstName ? null : "Required");
    setLastNameError(trimmedLastName ? null : "Required");
    setEmailError(trimmedEmail ? null : "Required");
    setCompanyError(trimmedCompany ? null : "Required");

    // Validation
    if (
      !trimmedFirstName ||
      !trimmedLastName ||
      !trimmedEmail ||
      !trimmedCompany ||
      !password ||
      !confirmPassword
    ) {
      setFormError("Please fill in all required fields");
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setFormError("Please enter a valid email address");
      return;
    }

    const isStrongPassword =
      password.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /\d/.test(password) &&
      /[^A-Za-z0-9]/.test(password);

    if (!isStrongPassword) {
      setFormError("Please enter a strong password meeting all criteria");
      return;
    }

    if (password !== confirmPassword) {
      setFormError("Passwords do not match");
      return;
    }

    setShowAgreements(true);
  };

  const handleConfirmSignup = async () => {
    try {
      await signup({
        first_name: firstName,
        last_name: lastName,
        email,
        company,
        password,
        access_code: accessCode || "",
      });
      // Navigation happens automatically via useEffect when isAuthenticated becomes true
    } catch (err) {
      // Error is handled by the store
      setShowAgreements(false);
    }
  };

  const displayError = formError || error;
  const isDuplicateEmailError =
    typeof displayError === "string" &&
    /already registered|already exists/i.test(displayError);

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#f8fafc] dark:bg-background">
      {/* LEFT COLUMN: Branding & Visuals (lg screens only) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-grey-50 dark:bg-grey-900/40 text-grey-900 dark:text-white flex-col justify-between p-16 border-r border-grey-200 dark:border-grey-800/60">
        {/* DotField Background */}
        <div className="absolute inset-0 z-0 pointer-events-none opacity-100">
          <DotField
            dotRadius={1.6}
            dotSpacing={20}
            cursorRadius={250}
            cursorForce={0.06}
            glowRadius={0}
            gradientFrom="rgba(56, 80, 160, 0.85)"
            gradientTo="rgba(28, 40, 80, 0.75)"
            glowColor="transparent"
          />
        </div>
        <div className="relative z-10 flex items-center">
          <Logo variant="full" size="2xl" />
        </div>

        {/* Center Visuals & Feature highlights */}
        <div className="relative z-10 my-auto flex flex-col gap-10 max-w-md">
          {/* Header Typography */}
          <div className="flex flex-col gap-3">
            <h2 className="text-2xl font-bold text-grey-900 dark:text-white tracking-tight leading-tight">
              Quantum ML & Orchestration Platform.
            </h2>
            <p className="text-grey-600 dark:text-grey-400 text-sm mt-1 leading-relaxed">
              QuantumCue is a no-code, end-to-end quantum orchestration platform
              for building, training, benchmarking, and deploying quantum
              machine learning models across leading quantum providers.
            </p>
          </div>

          {/* Minimalist Feature Highlight List */}
          <div className="flex flex-col gap-4 mt-4">
            <div className="bg-white/90 dark:bg-grey-900/90 backdrop-blur-sm rounded-xl p-4 border border-grey-200/60 dark:border-grey-800/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] flex items-start gap-4">
              <div className="text-[#3850A0] dark:text-brand-300 flex-shrink-0 mt-1">
                <Cpu size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-grey-800 dark:text-grey-100">
                  Unified Gateway
                </h3>
                <p className="text-xs text-grey-500 dark:text-grey-400 mt-0.5 leading-relaxed">
                  Deploy workflows seamlessly across top hardware providers.
                </p>
              </div>
            </div>

            <div className="bg-white/90 dark:bg-grey-900/90 backdrop-blur-sm rounded-xl p-4 border border-grey-200/60 dark:border-grey-800/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] flex items-start gap-4">
              <div className="text-[#3850A0] dark:text-brand-300 flex-shrink-0 mt-1">
                <Brain size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-grey-800 dark:text-grey-100">
                  AI-Guided Orchestration
                </h3>
                <p className="text-xs text-grey-500 dark:text-grey-400 mt-0.5 leading-relaxed">
                  Interact with your Copilot to optimize your quantum trained
                  machine learning models instantly.
                </p>
              </div>
            </div>

            <div className="bg-white/90 dark:bg-grey-900/90 backdrop-blur-sm rounded-xl p-4 border border-grey-200/60 dark:border-grey-800/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] flex items-start gap-4">
              <div className="text-[#3850A0] dark:text-brand-300 flex-shrink-0 mt-1">
                <Sparkles size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-grey-800 dark:text-grey-100">
                  Zero-Code Curation
                </h3>
                <p className="text-xs text-grey-500 dark:text-grey-400 mt-0.5 leading-relaxed">
                  Upload, clean, and auto-label datasets without a single line
                  of code.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom brand footer spacing */}
        <div className="relative z-10 text-xs text-grey-400 dark:text-grey-500">
          © {new Date().getFullYear()} QuantumCue. All rights reserved.
        </div>
      </div>

      {/* RIGHT COLUMN: Form & Sign-Up */}
      <div className="flex-1 flex flex-col justify-center items-center p-6 sm:p-12 md:p-16 lg:w-1/2 bg-white dark:bg-grey-950 min-h-screen">
        <div className="w-full max-w-[440px] flex flex-col justify-between h-full min-h-[500px]">
          <div className="h-6" />

          <div className="my-auto w-full">
            {/* Header text */}
            <div className="mb-6 text-center lg:text-left">
              <h1 className="text-2xl font-bold text-grey-900 dark:text-white tracking-tight">
                Create your account
              </h1>
              <p className="text-sm text-grey-500 dark:text-grey-400 mt-2">
                Get started with QuantumCue
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {displayError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="text-sm text-red-700">
                    <p>{displayError}</p>
                    {isDuplicateEmailError && (
                      <p className="mt-2">
                        <Link
                          to="/login"
                          className="underline font-medium text-red-800 hover:text-red-900"
                        >
                          Sign in
                        </Link>{" "}
                        instead.
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  type="text"
                  name="firstName"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    if (hasSubmitted) {
                      setFirstNameError(e.target.value.trim() ? null : "Required");
                    }
                  }}
                  placeholder="John"
                  autoComplete="given-name"
                  disabled={isLoading}
                  required
                  error={firstNameError || undefined}
                />
                <Input
                  label="Last Name"
                  type="text"
                  name="lastName"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    if (hasSubmitted) {
                      setLastNameError(e.target.value.trim() ? null : "Required");
                    }
                  }}
                  placeholder="Doe"
                  autoComplete="family-name"
                  disabled={isLoading}
                  required
                  error={lastNameError || undefined}
                />
              </div>

              <Input
                label="Email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (hasSubmitted) {
                    setEmailError(e.target.value.trim() ? null : "Required");
                  }
                }}
                placeholder="john.doe@company.com"
                autoComplete="email"
                disabled={isLoading}
                required
                error={emailError || undefined}
              />

              <Input
                label="Company"
                type="text"
                name="company"
                value={company}
                onChange={(e) => {
                  setCompany(e.target.value);
                  if (hasSubmitted) {
                    setCompanyError(e.target.value.trim() ? null : "Required");
                  }
                }}
                placeholder="Acme Corp"
                autoComplete="organization"
                disabled={isLoading}
                required
                error={companyError || undefined}
              />

              <div>
                <Input
                  label="Password"
                  type="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  disabled={isLoading}
                />
                <PasswordStrengthIndicator password={password} />
              </div>

              <Input
                label="Confirm Password"
                type="password"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                autoComplete="new-password"
                disabled={isLoading}
              />

              <Button
                type="submit"
                className="w-full"
                size="lg"
                isLoading={isLoading}
                variant="luxury"
              >
                Create account
              </Button>
            </form>

            <div className="mt-6 text-center lg:text-left">
              <p className="text-sm text-grey-500 dark:text-grey-400">
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="font-semibold hover:underline transition-colors"
                  style={{ color: "#3850A0" }}
                >
                  Sign in
                </Link>
              </p>
            </div>
          </div>

          <div className="h-6" />
        </div>
      </div>

      <Modal
        isOpen={showAgreements}
        onClose={() => setShowAgreements(false)}
        title="Terms and Conditions"
        description="Please scroll through the text below and carefully read the complete Terms and Conditions before signing up for the Leap service."
        size="lg"
      >
        <div className="flex flex-col gap-6 mt-4">
          <div className="h-48 overflow-y-auto p-4 bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-700/50 rounded-md text-sm text-grey-700 dark:text-grey-300 leading-relaxed">
            <h2 className="text-lg font-bold mb-2">
              QUANTUMCUE TERMS AND CONDITIONS
            </h2>
            <p className="mb-4">
              Please read these Terms and Conditions carefully before accessing
              or using the QuantumCue platform and related services.
            </p>
            <p className="mb-4 font-semibold">
              THIS CUSTOMER AGREEMENT (this “Agreement”) is entered into between
              QuantumCue, Inc., a Delaware corporation with its principal office
              in New York, New York (“QuantumCue,” “Company,” “we,” “us,” or
              “our”), and the individual or entity accepting this Agreement
              (“Customer,” “you,” or “your”), and governs your access to and use
              of the Services as of the date you accept this Agreement,
              including by clicking to accept, creating an account, accessing
              the platform, or executing an Order (the “Effective Date”).
            </p>
            <p className="mb-4">
              By accepting this Agreement on behalf of a company, university,
              nonprofit, governmental body, or other legal entity, you represent
              and warrant that you have full authority to bind that entity to
              this Agreement. If you do not have such authority, or do not agree
              to these terms, you must not accept this Agreement or access or
              use the Services.
            </p>
            <p className="mb-4">
              You must be at least eighteen (18) years old and legally capable
              of entering into a binding agreement to use the Services.
            </p>
            <p className="mb-6 italic">
              This Agreement was last updated on March 15, 2026.
              <br />
              Capitalized terms used in this Agreement have the meanings set
              forth in Section 9 or elsewhere in this Agreement.
            </p>

            <h3 className="font-bold text-base mt-6 mb-2">1. SERVICES</h3>
            <div className="pl-4 mb-6 space-y-4">
              <div>
                <h4 className="font-semibold">1.1 Services</h4>
                <p>
                  Subject to this Agreement and any applicable Order, QuantumCue
                  will provide Customer access to the products and services made
                  available by QuantumCue from time to time, including its
                  hosted no-code quantum workflow orchestration platform,
                  integrated provider access, benchmarking tools, data and model
                  workflow tools, job execution tools, dashboards, APIs,
                  documentation, software, support, and related services
                  (collectively, the “Services”).
                </p>
                <p className="mt-2">
                  QuantumCue is a hosted orchestration and access platform that
                  may enable Customer to configure, submit, monitor, compare,
                  and manage classical, hybrid, and quantum-related workloads
                  across one or more third-party computing providers or
                  integrated environments.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">1.2 Nature of Services</h4>
                <p>
                  Customer acknowledges that the Services may include access to
                  or interoperability with third-party computing environments,
                  quantum hardware providers, simulators, classical compute
                  resources, APIs, software libraries, datasets, and related
                  tools that are not owned or operated by QuantumCue.
                  Availability, performance, queue times, throughput, results,
                  solver access, hardware characteristics, and provider-specific
                  functionality may vary and may change without notice based on
                  third-party availability, maintenance, usage priority,
                  physical system constraints, export restrictions, technical
                  limitations, or changes imposed by those providers.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">1.3 Changes to Services</h4>
                <p>
                  QuantumCue may modify, improve, update, replace, suspend,
                  discontinue, or remove any part of the Services, features,
                  integrations, workflows, user interfaces, or functionality
                  from time to time. If a material reduction in core
                  functionality is made to a paid Service, QuantumCue will use
                  commercially reasonable efforts to provide prior notice.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">
                  1.4 No Guaranteed Availability of Specific Resources
                </h4>
                <p>
                  Unless expressly stated in an applicable Order, QuantumCue
                  does not guarantee the availability of any particular
                  third-party provider, quantum processor, solver, topology,
                  number of qubits, backend configuration, simulator, classical
                  compute environment, execution window, response time, or
                  uninterrupted access. Customer further acknowledges that
                  submitted jobs may be queued, delayed, throttled, retried,
                  rejected, or reprioritized.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">
                  1.5 Beta, Preview, and Experimental Features
                </h4>
                <p>
                  QuantumCue may make available certain alpha, beta, preview,
                  experimental, early-access, sandbox, or evaluation features
                  (“Beta Services”). Beta Services are provided solely for
                  testing and evaluation, may be incomplete or unstable, may
                  change at any time, and may be discontinued without notice.
                  Beta Services are provided strictly “AS IS” and “AS
                  AVAILABLE.”
                </p>
              </div>
            </div>

            <h3 className="font-bold text-base mt-6 mb-2">
              2. CUSTOMER ACCOUNTS AND RESPONSIBILITIES
            </h3>
            <div className="pl-4 mb-6 space-y-4">
              <div>
                <h4 className="font-semibold">2.1 Account Registration</h4>
                <p>
                  To access the Services, Customer may be required to create an
                  account and provide complete, accurate, and current account
                  information. Customer agrees to keep all registration and
                  account information up to date.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">2.2 Account Security</h4>
                <p>
                  Customer is solely responsible for maintaining the
                  confidentiality and security of its login credentials, API
                  keys, tokens, passwords, and all other access credentials.
                  Customer will promptly notify QuantumCue of any unauthorized
                  access to or use of Customer’s account or credentials.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">2.3 Responsibility for Users</h4>
                <p>
                  Customer is responsible for all acts and omissions of its
                  employees, agents, contractors, students, affiliates,
                  representatives, administrators, and any other users accessing
                  the Services through Customer’s account or under Customer’s
                  authority (collectively, “Users”). Customer will ensure that
                  all Users comply with this Agreement.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">
                  2.4 Accurate Identity; No Impersonation
                </h4>
                <p>
                  Customer may not impersonate another person or entity, create
                  accounts using false or misleading information, or
                  misrepresent affiliation with any organization, provider,
                  institution, or government agency.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">
                  2.5 Customer Systems and Security
                </h4>
                <p>
                  Customer is responsible for obtaining and maintaining all
                  hardware, software, network connectivity, browsers, devices,
                  internal systems, permissions, and security controls necessary
                  to access and use the Services.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">2.6 Customer Data and Backups</h4>
                <p>
                  Customer is solely responsible for its prompts, datasets,
                  code, models, workflows, configurations, job submissions,
                  files, results, exports, reports, and other data or materials
                  submitted to or processed through the Services (“Customer
                  Data”), including maintaining all necessary backups and
                  archival copies.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">
                  2.7 Regulated and Sensitive Data
                </h4>
                <p>
                  Unless expressly authorized in writing by QuantumCue, Customer
                  will not upload, submit, transmit, store, or otherwise make
                  available through the Services:
                </p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>(a) protected health information subject to HIPAA;</li>
                  <li>(b) payment card data subject to PCI-DSS;</li>
                  <li>(c) nonpublic personal financial information;</li>
                  <li>
                    (d) Social Security numbers, passport numbers, driver’s
                    license numbers, or similar national identifiers;
                  </li>
                  <li>
                    (e) special-category or sensitive personal data under
                    applicable privacy laws;
                  </li>
                  <li>
                    (f) classified information, export-controlled technical
                    data, or defense-related controlled data; or
                  </li>
                  <li>
                    (g) any data subject to legal, regulatory, contractual, or
                    institutional restrictions inconsistent with the intended
                    use of the Services.
                  </li>
                </ul>
              </div>
            </div>

            <h3 className="font-bold text-base mt-6 mb-2">
              3. SERVICE USE; ORDERS; RESTRICTIONS
            </h3>
            <div className="pl-4 mb-6 space-y-4">
              <div>
                <h4 className="font-semibold">3.1 Service Offerings</h4>
                <p>
                  QuantumCue may offer the Services on a free, trial, pilot,
                  paid, enterprise, academic, startup, nonprofit, or custom
                  basis, as specified in an applicable order form, statement of
                  work, online plan, subscription page, proposal, or other
                  ordering document accepted by both parties or accepted online
                  by Customer (each, “Order”).
                </p>
              </div>

              <div>
                <h4 className="font-semibold">3.2 Scope of Access</h4>
                <p>
                  Customer may access and use only those Services, plans,
                  features, environments, provider integrations, credits, or
                  usage allotments expressly included in its account tier or
                  applicable Order.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">3.3 Usage Limits</h4>
                <p>
                  The Services may be subject to usage limitations, including
                  limits on users, projects, storage, API calls, workflow runs,
                  compute submissions, credits, provider access, throughput,
                  concurrency, output retention, support levels, or other
                  metrics. Customer will not exceed the usage limits applicable
                  to its plan or Order.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">3.4 Fees and Credits</h4>
                <p>
                  If the Services are paid, Customer agrees to pay all fees,
                  charges, subscription amounts, platform fees, usage fees,
                  overage fees, provider pass-through costs, taxes, and other
                  amounts specified in the applicable Order or pricing page.
                  Unless otherwise stated:
                </p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>(a) fees are in U.S. dollars;</li>
                  <li>(b) fees are non-cancelable and non-refundable;</li>
                  <li>
                    (c) prepaid credits, promotional credits, trial credits, and
                    platform credits have no cash value and are not refundable;
                    and
                  </li>
                  <li>
                    (d) unused credits or allowances expire as stated in the
                    applicable Order or plan terms.
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold">
                  3.5 Third-Party Pass-Through Costs
                </h4>
                <p>
                  QuantumCue may pass through charges, metering, usage costs,
                  provider fees, or infrastructure fees arising from Customer’s
                  use of third-party integrations, compute providers, or
                  external environments. Customer is responsible for such
                  amounts where disclosed in an applicable Order, pricing
                  schedule, or service description.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">3.6 Prohibited Uses</h4>
                <p>
                  Customer will not, and will not permit any User or third party
                  to:
                </p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>
                    (a) reverse engineer, decompile, disassemble, decode, copy,
                    or attempt to derive source code, trade secrets, or
                    underlying ideas from the Services, except to the limited
                    extent expressly prohibited from restriction by applicable
                    law;
                  </li>
                  <li>
                    (b) modify, adapt, translate, or create derivative works of
                    the Services except as expressly permitted by QuantumCue in
                    writing;
                  </li>
                  <li>
                    (c) resell, sublicense, lease, time-share, distribute,
                    outsource, or provide the Services to third parties on a
                    service bureau or managed service basis without QuantumCue’s
                    prior written consent;
                  </li>
                  <li>
                    (d) use the Services for any unlawful, fraudulent,
                    deceptive, abusive, or unauthorized purpose;
                  </li>
                  <li>
                    (e) upload malicious code, malware, spyware, ransomware, or
                    harmful scripts;
                  </li>
                  <li>
                    (f) probe, scan, exploit, or test the vulnerability of the
                    Services or related systems without authorization;
                  </li>
                  <li>
                    (g) interfere with or disrupt the integrity, security,
                    performance, or operation of the Services or third-party
                    systems connected to the Services;
                  </li>
                  <li>
                    (h) circumvent account limits, access controls, technical
                    restrictions, metering mechanisms, or usage policies;
                  </li>
                  <li>
                    (i) use automated scraping, bots, spiders, or harvesting
                    tools against the Services except as expressly authorized
                    through documented APIs;
                  </li>
                  <li>
                    (j) use the Services in violation of export control,
                    sanctions, privacy, intellectual property, or other
                    applicable laws;
                  </li>
                  <li>
                    (k) use the Services to develop or benchmark a competing
                    platform without QuantumCue’s prior written consent;
                  </li>
                  <li>
                    (l) use the Services in connection with weapons systems,
                    mass surveillance, unlawful discrimination, critical
                    life-support systems, or any application where failure could
                    reasonably result in death, bodily injury, or severe
                    property or environmental damage; or
                  </li>
                  <li>
                    (m) upload or process Customer Data for which Customer lacks
                    all necessary rights, permissions, consents, or legal
                    authority.
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold">3.7 Suspension</h4>
                <p>
                  QuantumCue may suspend or restrict access to the Services
                  immediately, with or without prior notice, if QuantumCue
                  reasonably believes:
                </p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>(a) Customer or any User has violated this Agreement;</li>
                  <li>
                    (b) the Services are being used in a manner that threatens
                    security, integrity, legality, or availability;
                  </li>
                  <li>
                    (c) suspension is required by law, court order, sanctions,
                    export restrictions, or provider requirements; or
                  </li>
                  <li>
                    (d) Customer has failed to pay undisputed amounts when due.
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold">3.8 Export Compliance</h4>
                <p>
                  Customer will comply with all applicable U.S. export control,
                  sanctions, and trade laws and regulations in connection with
                  its access to and use of the Services. Customer represents and
                  warrants that neither it nor any User is:
                </p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>
                    (a) located in, organized in, or ordinarily resident in any
                    country or territory subject to comprehensive U.S. embargo
                    or sanctions;
                  </li>
                  <li>
                    (b) identified on any U.S. government denied-party or
                    restricted-party list; or
                  </li>
                  <li>(c) using the Services for any prohibited end use.</li>
                </ul>
                <p className="mt-2">
                  Customer will not export, re-export, transfer, release, or
                  permit access to the Services or related technical data in
                  violation of applicable law.
                </p>
              </div>
            </div>

            <h3 className="font-bold text-base mt-6 mb-2">
              4. CONFIDENTIALITY, PRIVACY, AND DATA
            </h3>
            <div className="pl-4 mb-6 space-y-4">
              <div>
                <h4 className="font-semibold">4.1 Confidential Information</h4>
                <p>
                  “Confidential Information” means any non-public information
                  disclosed by one party (“Disclosing Party”) to the other
                  (“Receiving Party”) that is designated as confidential or that
                  reasonably should be understood to be confidential given the
                  nature of the information and the circumstances of disclosure,
                  including product plans, architecture, software, technical
                  information, credentials, business information, pricing,
                  security information, non-public documentation, and customer
                  or provider information.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">4.2 Exclusions</h4>
                <p>
                  Confidential Information does not include information that the
                  Receiving Party can demonstrate:
                </p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>
                    (a) is or becomes publicly available without breach of this
                    Agreement;
                  </li>
                  <li>
                    (b) was already lawfully known to the Receiving Party
                    without restriction;
                  </li>
                  <li>
                    (c) is lawfully obtained from a third party without
                    restriction; or
                  </li>
                  <li>
                    (d) was independently developed without use of or reference
                    to the Disclosing Party’s Confidential Information.
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold">
                  4.3 Confidentiality Obligations
                </h4>
                <p>The Receiving Party will:</p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>
                    (a) use the Disclosing Party’s Confidential Information only
                    as necessary to exercise its rights or perform its
                    obligations under this Agreement;
                  </li>
                  <li>
                    (b) protect such Confidential Information using at least
                    reasonable care; and
                  </li>
                  <li>
                    (c) disclose it only to personnel, contractors, professional
                    advisors, and service providers who have a need to know and
                    are bound by confidentiality obligations at least as
                    protective as those in this Agreement.
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold">4.4 Compelled Disclosure</h4>
                <p>
                  The Receiving Party may disclose Confidential Information to
                  the extent required by law, regulation, subpoena, or court
                  order, provided it gives prior notice where legally permitted
                  and reasonably cooperates, at the Disclosing Party’s expense,
                  in seeking confidential treatment or protective relief.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">
                  4.5 QuantumCue Confidential Information
                </h4>
                <p>
                  Without limitation, the Services, non-public platform
                  features, documentation, source code, APIs, pricing,
                  architecture, credentials, tokens, and security information of
                  QuantumCue are QuantumCue Confidential Information.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">4.6 Privacy</h4>
                <p>
                  Customer acknowledges that QuantumCue may collect and process
                  Account Information, usage data, support data, technical logs,
                  telemetry, and other information relating to the
                  administration, security, support, operation, and improvement
                  of the Services in accordance with QuantumCue’s Privacy
                  Policy, available at [INSERT PRIVACY POLICY URL].
                </p>
              </div>

              <div>
                <h4 className="font-semibold">4.7 Customer Data Processing</h4>
                <p>
                  To the extent QuantumCue processes personal data on behalf of
                  Customer, the parties may enter into a separate Data
                  Processing Addendum (“DPA”) if required by applicable law or
                  by Customer’s compliance requirements. Unless otherwise
                  expressly agreed in writing, Customer remains the controller
                  of any personal data included in Customer Data, and Customer
                  is solely responsible for providing required notices and
                  obtaining necessary consents.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">4.8 Security</h4>
                <p>
                  QuantumCue will use commercially reasonable administrative,
                  technical, and organizational safeguards designed to protect
                  Customer Data against unauthorized access, use, alteration, or
                  disclosure. However, no system can be guaranteed to be
                  completely secure, and QuantumCue does not guarantee that
                  unauthorized third parties will never defeat such measures.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">4.9 Aggregated and Usage Data</h4>
                <p>
                  QuantumCue may collect, generate, and use aggregated,
                  de-identified, statistical, operational, telemetry, and usage
                  data derived from the use of the Services for lawful business
                  purposes, including analytics, platform improvement, security,
                  capacity planning, benchmarking, and product development,
                  provided such data does not identify Customer as the source.
                </p>
              </div>
            </div>

            <h3 className="font-bold text-base mt-6 mb-2">
              5. INTELLECTUAL PROPERTY
            </h3>
            <div className="pl-4 mb-6 space-y-4">
              <div>
                <h4 className="font-semibold">5.1 QuantumCue IP</h4>
                <p>
                  As between the parties, QuantumCue and its licensors retain
                  all right, title, and interest in and to the Services,
                  platform, software, workflows, interfaces, documentation,
                  configurations, templates, algorithms, technology, designs,
                  and all related intellectual property rights (collectively,
                  “QuantumCue IP”). Except for the limited rights expressly
                  granted in this Agreement, no rights are granted to Customer
                  by license, implication, estoppel, or otherwise.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">
                  5.2 Limited License to Customer
                </h4>
                <p>
                  Subject to this Agreement, QuantumCue grants Customer a
                  limited, non-exclusive, non-transferable, non-sublicensable
                  right during the Term to access and use the Services and
                  applicable documentation solely for Customer’s internal
                  business, research, educational, or evaluation purposes, as
                  permitted by the applicable plan or Order.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">5.3 Customer Data</h4>
                <p>
                  As between the parties, Customer retains all right, title, and
                  interest in and to Customer Data. Customer grants QuantumCue a
                  non-exclusive, worldwide, limited license to host, copy,
                  transmit, process, display, transform, execute, and otherwise
                  use Customer Data solely as necessary to provide, secure,
                  support, maintain, and improve the Services and to perform
                  this Agreement.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">5.4 Output and Results</h4>
                <p>
                  As between the parties, and subject to third-party provider
                  terms applicable to integrated environments, Customer owns the
                  specific results, outputs, reports, and job results generated
                  from Customer’s use of the Services using its Customer Data
                  (“Customer Results”), excluding any QuantumCue IP, generalized
                  know-how, underlying software, models, templates, platform
                  logic, or analytics incorporated therein.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">5.5 Feedback</h4>
                <p>
                  If Customer provides suggestions, ideas, comments, enhancement
                  requests, recommendations, or other feedback regarding the
                  Services (“Feedback”), Customer grants QuantumCue a perpetual,
                  irrevocable, worldwide, royalty-free, fully paid-up right and
                  license to use, incorporate, modify, disclose, and exploit
                  such Feedback for any lawful purpose without restriction or
                  obligation.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">
                  5.6 Third-Party Software and Provider Terms
                </h4>
                <p>
                  The Services may include or interoperate with third-party
                  software, APIs, libraries, open-source components, cloud
                  services, or computing providers. Such components may be
                  subject to separate terms, license conditions, or usage
                  policies, and Customer agrees to comply with all such terms to
                  the extent applicable to Customer’s use.
                </p>
              </div>
            </div>

            <h3 className="font-bold text-base mt-6 mb-2">
              6. TERM; TERMINATION
            </h3>
            <div className="pl-4 mb-6 space-y-4">
              <div>
                <h4 className="font-semibold">6.1 Term</h4>
                <p>
                  This Agreement begins on the Effective Date and continues
                  until terminated in accordance with this Section 6 (the
                  “Term”).
                </p>
              </div>

              <div>
                <h4 className="font-semibold">
                  6.2 Termination for Convenience
                </h4>
                <p>
                  Unless an applicable Order provides otherwise, either party
                  may terminate this Agreement for convenience upon seven (7)
                  days’ prior written notice.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">6.3 Termination for Cause</h4>
                <p>
                  Either party may terminate this Agreement upon written notice
                  if the other party materially breaches this Agreement and
                  fails to cure such breach within seven (7) days after
                  receiving written notice describing the breach.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">
                  6.4 Immediate Termination by QuantumCue
                </h4>
                <p>
                  QuantumCue may terminate this Agreement or suspend/terminate
                  access immediately upon notice if:
                </p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>
                    (a) Customer materially breaches Sections 2, 3, 4, or 5;
                  </li>
                  <li>
                    (b) continued provision of the Services would create legal,
                    regulatory, security, sanctions, provider, or operational
                    risk;
                  </li>
                  <li>
                    (c) Customer becomes insolvent, ceases operations, or
                    becomes subject to bankruptcy, receivership, liquidation, or
                    similar proceedings; or
                  </li>
                  <li>
                    (d) QuantumCue is required to do so by law or by a
                    third-party provider whose services are necessary to deliver
                    the Services.
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold">6.5 Effect of Termination</h4>
                <p>Upon termination or expiration:</p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>
                    (a) Customer’s right to access and use the Services
                    immediately ends;
                  </li>
                  <li>
                    (b) Customer must cease all use of the Services and
                    QuantumCue IP;
                  </li>
                  <li>
                    (c) each party will, upon request, return or destroy the
                    other party’s Confidential Information, except as required
                    by law or reasonably retained in secure backups or legal
                    archives; and
                  </li>
                  <li>
                    (d) any accrued payment obligations remain due and payable.
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold">
                  6.6 Retrieval of Customer Data
                </h4>
                <p>
                  Subject to the applicable Order and QuantumCue’s standard
                  retention practices, QuantumCue may provide Customer a limited
                  post-termination period, if any, to retrieve Customer Data.
                  After that period, QuantumCue may delete Customer Data unless
                  required by law to retain it.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">6.7 Survival</h4>
                <p>
                  Sections 3.6, 3.8, 4, 5, 6.5, 6.6, 7, 8, and 9, together with
                  any payment obligations accrued prior to termination, will
                  survive termination or expiration of this Agreement.
                </p>
              </div>
            </div>

            <h3 className="font-bold text-base mt-6 mb-2">
              7. DISCLAIMERS; LIMITATION OF LIABILITY; INDEMNITY
            </h3>
            <div className="pl-4 mb-6 space-y-4">
              <div>
                <h4 className="font-semibold">7.1 No Warranty</h4>
                <p className="uppercase">
                  EXCEPT AS EXPRESSLY SET FORTH IN THIS AGREEMENT OR AN
                  APPLICABLE ORDER, THE SERVICES, INCLUDING ALL PLATFORM
                  FEATURES, THIRD-PARTY INTEGRATIONS, COMPUTE ACCESS, RESULTS,
                  OUTPUTS, DOCUMENTATION, BETA SERVICES, AND RELATED MATERIALS,
                  ARE PROVIDED “AS IS” AND “AS AVAILABLE.”
                </p>
                <p className="uppercase mt-2">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, QUANTUMCUE DISCLAIMS
                  ALL WARRANTIES, CONDITIONS, REPRESENTATIONS, AND GUARANTEES OF
                  ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE,
                  INCLUDING ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
                  FOR A PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT,
                  SATISFACTORY QUALITY, ACCURACY, RELIABILITY, AVAILABILITY, OR
                  THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, SECURE,
                  OR PRODUCE ANY PARTICULAR RESULT.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">
                  7.2 No Guarantee of Scientific, Technical, or Commercial
                  Outcomes
                </h4>
                <p>
                  Customer acknowledges that quantum, hybrid, and advanced
                  computational workflows are inherently experimental and may
                  produce variable, probabilistic, incomplete, unexpected,
                  delayed, or non-actionable results. QuantumCue does not
                  warrant that use of the Services will achieve any scientific,
                  engineering, research, development, commercial, operational,
                  or business objective.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">7.3 Third-Party Providers</h4>
                <p>
                  QuantumCue is not responsible for downtime, inaccuracy, queue
                  delays, performance failures, changed functionality, pricing
                  changes, discontinued access, or acts or omissions of any
                  third-party provider integrated with or accessible through the
                  Services.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">7.4 Limitation of Liability</h4>
                <p className="uppercase">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW:
                </p>
                <ul className="list-disc pl-5 mt-2 space-y-1 uppercase">
                  <li>
                    (a) QUANTUMCUE AND ITS OFFICERS, DIRECTORS, EMPLOYEES,
                    AFFILIATES, LICENSORS, CONTRACTORS, AND SUPPLIERS WILL NOT
                    BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY,
                    PUNITIVE, OR CONSEQUENTIAL DAMAGES, OR FOR ANY LOSS OF
                    PROFITS, REVENUE, BUSINESS, OPPORTUNITY, GOODWILL, DATA, OR
                    USE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES;
                  </li>
                  <li>
                    (b) QUANTUMCUE WILL NOT BE LIABLE FOR ANY DAMAGES ARISING
                    OUT OF OR RELATING TO THIRD-PARTY PROVIDERS, CUSTOMER DATA,
                    CUSTOMER RESULTS, DELAYS, QUEUING, SUSPENSIONS, EXPORT
                    RESTRICTIONS, SECURITY INCIDENTS NOT CAUSED BY QUANTUMCUE’S
                    BREACH OF THIS AGREEMENT, OR CUSTOMER’S FAILURE TO BACK UP
                    ITS DATA; AND
                  </li>
                  <li>
                    (c) QUANTUMCUE’S AGGREGATE LIABILITY ARISING OUT OF OR
                    RELATING TO THIS AGREEMENT WILL NOT EXCEED THE GREATER OF:
                    (I) THE AMOUNT PAID BY CUSTOMER TO QUANTUMCUE UNDER THIS
                    AGREEMENT IN THE TWELVE (12) MONTHS PRECEDING THE EVENT
                    GIVING RISE TO THE CLAIM, OR (II) ONE HUNDRED U.S. DOLLARS
                    (US $100).
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold">7.5 Basis of the Bargain</h4>
                <p>
                  The disclaimers and limitations in this Agreement form an
                  essential basis of the bargain between the parties and apply
                  notwithstanding any failure of essential purpose.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">7.6 Customer Indemnity</h4>
                <p>
                  Customer will defend, indemnify, and hold harmless QuantumCue
                  and its officers, directors, employees, affiliates,
                  contractors, licensors, and providers from and against any
                  third-party claims, actions, proceedings, damages,
                  liabilities, judgments, settlements, fines, penalties, costs,
                  and expenses (including reasonable attorneys’ fees) arising
                  out of or related to:
                </p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>(a) Customer Data;</li>
                  <li>
                    (b) Customer’s or any User’s use of the Services in
                    violation of this Agreement;
                  </li>
                  <li>
                    (c) Customer’s or any User’s violation of law, regulation,
                    sanctions, export controls, privacy obligations, or
                    third-party rights; or
                  </li>
                  <li>
                    (d) any claim that Customer Data or Customer’s use of the
                    Services infringes, misappropriates, or violates any
                    third-party rights.
                  </li>
                </ul>
              </div>
            </div>

            <h3 className="font-bold text-base mt-6 mb-2">
              8. GENERAL PROVISIONS
            </h3>
            <div className="pl-4 mb-6 space-y-4">
              <div>
                <h4 className="font-semibold">8.1 Entire Agreement</h4>
                <p>
                  This Agreement, together with any Orders, DPA, and documents
                  expressly incorporated by reference, constitutes the complete
                  and exclusive agreement between the parties regarding the
                  subject matter hereof and supersedes all prior or
                   contemporaneous oral or written understandings.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">8.2 Order of Precedence</h4>
                <p>
                  In the event of a conflict between this Agreement and an
                  Order, the Order will control only to the extent it expressly
                  states an intent to override this Agreement with respect to
                  the specific subject matter at issue.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">8.3 Independent Contractors</h4>
                <p>
                  The parties are independent contractors. Nothing in this
                  Agreement creates any partnership, joint venture, agency,
                  fiduciary, franchise, employment, or similar relationship.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">8.4 Force Majeure</h4>
                <p>
                  QuantumCue will not be liable for any delay, failure, or
                  interruption caused by circumstances beyond its reasonable
                  control, including acts of God, natural disasters, war,
                  terrorism, civil unrest, labor disputes, internet or
                  telecommunications failures, power outages, epidemics,
                  governmental actions, sanctions, export restrictions, cloud
                  outages, or failure or delay of third-party providers.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">8.5 Assignment</h4>
                <p>
                  Customer may not assign, delegate, or transfer this Agreement,
                  in whole or in part, without QuantumCue’s prior written
                  consent. Any attempted assignment in violation of this Section
                  is void. QuantumCue may assign this Agreement in connection
                  with a merger, financing, reorganization, sale of assets, or
                  change of control, or to an affiliate.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">8.6 Subcontractors</h4>
                <p>
                  QuantumCue may use affiliates, contractors, subprocessors,
                  hosting providers, or other service providers to perform
                  portions of the Services, provided QuantumCue remains
                  responsible for its obligations under this Agreement to the
                  extent required by law.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">8.7 No Waiver</h4>
                <p>
                  No failure or delay by either party in exercising any right
                  under this Agreement will operate as a waiver of that right.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">8.8 Severability</h4>
                <p>
                  If any provision of this Agreement is held invalid, illegal,
                  or unenforceable, that provision will be enforced to the
                  maximum extent permissible, and the remaining provisions will
                  remain in full force and effect.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">8.9 Amendments</h4>
                <p>
                  QuantumCue may modify this Agreement from time to time. If
                  QuantumCue makes a material change, QuantumCue will provide
                  reasonable notice, including by posting an updated version on
                  its website or through the Services. Unless otherwise stated,
                  the updated Agreement becomes effective on the date specified
                  in the notice. Customer’s continued use of the Services after
                  the effective date of the updated Agreement constitutes
                  acceptance of the revised terms.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">8.10 Governing Law</h4>
                <p>
                  This Agreement and any dispute arising out of or relating to
                  this Agreement will be governed by the laws of the State of
                  New York, without regard to conflict of laws principles.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">8.11 Venue; Jurisdiction</h4>
                <p>
                  The parties agree that any action or proceeding arising out of
                  or relating to this Agreement must be brought exclusively in
                  the state or federal courts located in New York County, New
                  York, and each party irrevocably submits to the personal
                  jurisdiction of those courts and waives any objection based on
                  venue or forum non conveniens.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">8.12 Notices</h4>
                <p>
                  All legal notices under this Agreement must be in writing and
                  sent:
                </p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>
                    (a) if to QuantumCue, to: [INSERT LEGAL NOTICE EMAIL] and
                    [INSERT NEW YORK OFFICE ADDRESS]; and
                  </li>
                  <li>
                    (b) if to Customer, to the email or physical address
                    associated with Customer’s account or specified in the
                    applicable Order.
                  </li>
                </ul>
                <p className="mt-2">
                  Notice is deemed given upon receipt if delivered by hand,
                  nationally recognized courier, or email.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">8.13 Publicity</h4>
                <p>
                  Unless otherwise agreed in writing, QuantumCue may identify
                  Customer’s name and logo in customer lists and general
                  marketing materials solely to indicate that Customer uses the
                  Services, unless Customer notifies QuantumCue in writing that
                  it does not consent.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">8.14 Further Assurances</h4>
                <p>
                  Each party will execute and deliver such further documents and
                  take such further actions as may be reasonably necessary to
                  carry out the purposes of this Agreement.
                </p>
              </div>
            </div>

            <h3 className="font-bold text-base mt-6 mb-2">9. DEFINITIONS</h3>
            <div className="pl-4 space-y-4">
              <p>For purposes of this Agreement:</p>

              <div>
                <h4 className="font-semibold">9.1 “Account Information”</h4>
                <p>
                  means information provided by or collected from Customer or
                  Users in connection with account creation, administration,
                  billing, security, or support.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">9.2 “Customer Data”</h4>
                <p>
                  means all data, inputs, code, prompts, files, models,
                  configurations, workflows, materials, and content submitted,
                  uploaded, transmitted, or otherwise provided by or on behalf
                  of Customer through the Services.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">9.3 “Customer Results”</h4>
                <p>
                  means the specific output, reports, job results, workflow
                  results, and returned execution artifacts generated for
                  Customer through the Services, excluding QuantumCue IP and
                  excluding generalized analytics or platform improvements.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">9.4 “Documentation”</h4>
                <p>
                  means QuantumCue’s user guides, technical documentation, help
                  materials, support pages, policies, and related written
                  materials made available by QuantumCue for the Services.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">9.5 “Order”</h4>
                <p>
                  means any order form, statement of work, online plan
                  selection, subscription, quotation, proposal, or other
                  ordering document accepted by both parties and referencing this
                  Agreement.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">9.6 “QuantumCue IP”</h4>
                <p>has the meaning set forth in Section 5.1.</p>
              </div>

              <div>
                <h4 className="font-semibold">9.7 “Services”</h4>
                <p>has the meaning set forth in Section 1.1.</p>
              </div>

              <div>
                <h4 className="font-semibold">9.8 “User”</h4>
                <p>
                  means Customer and any individual authorized by Customer to
                  access or use the Services through Customer’s account.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <Checkbox
              checked={agreedTerms}
              onChange={(e) => setAgreedTerms(e.target.checked)}
            >
              I agree to the{" "}
              <Link
                to="/terms"
                target="_blank"
                className="text-brand-50 dark:text-brand-50 font-medium hover:underline"
              >
                Terms & Conditions
              </Link>
            </Checkbox>

            <Checkbox
              checked={agreedPrivacy}
              onChange={(e) => setAgreedPrivacy(e.target.checked)}
            >
              I agree to the{" "}
              <Link
                to="/privacy"
                target="_blank"
                className="text-brand-50 dark:text-brand-50 font-medium hover:underline"
              >
                Privacy Policy
              </Link>
            </Checkbox>

            <Checkbox
              checked={agreedEula}
              onChange={(e) => setAgreedEula(e.target.checked)}
            >
              I agree to the{" "}
              <Link
                to="/eula"
                target="_blank"
                className="text-brand-50 dark:text-brand-50 font-medium hover:underline"
              >
                End User Agreement
              </Link>
            </Checkbox>
          </div>
        </div>

        <div className="mt-8">
          <Button
            className="w-full text-sm font-semibold tracking-wide uppercase py-3"
            onClick={handleConfirmSignup}
            disabled={
              !agreedTerms || !agreedPrivacy || !agreedEula || isLoading
            }
            isLoading={isLoading}
            type="button"
          >
            CREATE ACCOUNT
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Signup;
